import React, { useEffect, useState } from "react";
import { api } from "@/skate/lib/api";
import { useAuth } from "@/skate/context/AuthContext";
import { Link } from "react-router-dom";
import SkaterAvatar from "@/skate/components/SkaterAvatar";
import RankBadge from "@/skate/components/RankBadge";
import { tierForElo, FORMAT_LABEL } from "@/skate/lib/rank";
import { MapPin, Flame, Shield, Settings, LogOut } from "lucide-react";

const InfoChip = ({ label, value, accent = false }) => (
  <div className="border border-zinc-800 p-3">
    <div className="font-mono text-[9px] tracking-widest text-zinc-500">{label}</div>
    <div className={`font-display text-2xl mt-1 ${accent ? "text-[#FACC15]" : ""}`}>{value}</div>
  </div>
);

export const Profile = () => {
  const { user, logout } = useAuth();
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/matches?limit=30");
        setMatches(data);
      } catch {}
    })();
  }, []);

  if (!user) return null;
  const tier = tierForElo(user.elo);
  const wlRatio = user.wins + user.losses > 0 ? Math.round((user.wins / (user.wins + user.losses)) * 100) : 0;

  return (
    <div data-testid="profile-page">
      {/* Header card */}
      <div className="border border-zinc-800 bg-[#121212] p-6 mb-4">
        <div className="flex items-start gap-4">
          <SkaterAvatar user={user} size={80} className="!w-20 !h-20" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display text-3xl truncate">@{user.username}</span>
              {user.verified && <span className="font-mono text-[9px] tracking-widest text-[#FACC15] border border-[#FACC15] px-1.5">✓ VERIFIED</span>}
            </div>
            <div className="font-mono text-[10px] text-zinc-500 mt-1 tracking-wider flex items-center gap-2">
              <MapPin className="w-3 h-3" /> {user.city} · {user.stance?.toUpperCase()}
            </div>
            {user.bio && <p className="text-sm text-zinc-300 mt-3">{user.bio}</p>}
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 pt-6 border-t border-zinc-900">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500">{tier.name} TIER</div>
            <div className="font-display text-5xl mt-1" style={{color: tier.color}}>{user.elo}</div>
            <div className="font-mono text-[10px] text-zinc-500 tracking-widest">ELO</div>
          </div>
          <RankBadge elo={user.elo} size="lg" />
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <InfoChip label="WINS" value={user.wins} />
        <InfoChip label="LOSSES" value={user.losses} />
        <InfoChip label="W RATE" value={`${wlRatio}%`} accent />
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <InfoChip label="STREAK" value={user.current_streak} />
        <InfoChip label="BEST" value={user.best_streak} />
        <InfoChip label="REP" value={user.reputation} />
      </div>

      {/* Tricks */}
      {user.favorite_tricks?.length > 0 && (
        <div className="mb-6">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">ARSENAL</div>
          <div className="flex flex-wrap gap-2">
            {user.favorite_tricks.map((t, i) => (
              <span key={i} className="border border-zinc-800 px-3 py-1 font-mono text-xs">{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* Match history */}
      <div className="mb-6">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-3">MATCH HISTORY</div>
        {matches.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-8 text-center font-mono text-xs text-zinc-500 tracking-widest">
            NO MATCHES YET
          </div>
        ) : (
          <div className="border border-zinc-800 divide-y divide-zinc-900">
            {matches.map((m) => {
              const youWon = m.winner_id === user.user_id;
              const opp = youWon ? m.loser : m.winner;
              const delta = youWon ? m.winner_delta : m.loser_delta;
              return (
                <div key={m.match_id} className="p-4 flex items-center gap-3" data-testid={`profile-match-${m.match_id}`}>
                  <div className={`font-display text-2xl w-10 ${youWon ? "text-green-500" : "text-[#EF4444]"}`}>
                    {youWon ? "W" : "L"}
                  </div>
                  <SkaterAvatar user={opp} size={36} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base truncate">vs @{opp?.username}</div>
                    <div className="font-mono text-[10px] text-zinc-500">{FORMAT_LABEL[m.format]} · {m.location} · {m.status}</div>
                  </div>
                  {delta != null && m.status === "CONFIRMED" && (
                    <div className={`font-mono font-bold ${delta >= 0 ? "text-green-500" : "text-[#EF4444]"}`}>
                      {delta >= 0 ? "+" : ""}{delta}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button
        onClick={logout}
        data-testid="profile-logout"
        className="w-full border border-zinc-800 hover:border-[#FACC15] hover:text-[#FACC15] p-4 font-display tracking-widest flex items-center justify-center gap-2 transition-colors"
      >
        <LogOut className="w-4 h-4" /> LOG OUT
      </button>
    </div>
  );
};

export default Profile;
